const welcomePage = {
  navigationTitle: "Introduction",
  title: "Introduction",
  elements: [
    {
      type: "html",
      html: `
          <p style="margin-bottom: 24px;">
            <b>Welcome!</b> Thank you for being a part of this pilot. We believe that supporting teacher growth is the key to a thriving classroom, and your feedback is incredibly valuable as we work to build the best possible support for teachers.
          </p>
          <p>
            This confidential survey will take about <b>6-8 minutes</b> to complete. Your honest responses will help us understand your experiences and measure the effectiveness of HeyBodhi.
          </p>
          `,
    },
  ],
};

const teachingContextPage = {
  navigationTitle: "Your Teaching Context",
  title: "Your Teaching Context",
  description: "This helps us understand the experiences of different teachers.",
  elements: [
    {
      name: "teaching_context__primary_role",
      title: "What is your primary role?",
      type: "radiogroup",
      choices: [
        { value: "pre_service_or_student_teacher", text: "Pre-service/Student teacher" },
        { value: "classroom_teacher", text: "Classroom Teacher" },
        { value: "instructional_coach", text: "Instructional Coach" },
        { value: "administrative", text: "Administrative" },
      ],
      showOtherItem: true,
      otherText: "Other",
      otherPlaceholder: "Please specify",
      otherErrorText: "Please specify",
      isRequired: true,
    },
    {
      name: "teaching_context__grade",
      title: "What grade band do you primarily teach?",
      type: "radiogroup",
      choices: [
        { value: "pre_k_2", text: "Early Childhood (Pre-K–2)" },
        { value: "elementary_3_5", text: "Elementary (3–5)" },
        { value: "middle_school_6_8", text: "Middle School (6–8)" },
        { value: "high_school_9_12", text: "High School (9–12)" },
      ],
      isRequired: true,
    },
    {
      name: "teaching_context__experience_years",
      title: "How many years have you been teaching?",
      type: "radiogroup",
      choices: [
        { value: "0", text: "0 (Pre-service/Student Teacher)" },
        { value: "1", text: "1" },
        { value: "2_3", text: "2–3" },
        { value: "4_7", text: "4–7" },
        { value: "8_15", text: "8–15" },
        { value: "16_23", text: "16–23" },
        { value: "24_30", text: "24–30" },
        { value: "31_plus", text: "31+" },
      ],
      isRequired: true,
    },
    {
      name: "teaching_context__setting",
      title: "Which best describes your teaching setting? (Check all that apply)",
      type: "checkbox",
      choices: [
        { value: "public_school", text: "Public School" },
        { value: "private_school", text: "Private School" },
        { value: "rural_school", text: "Rural School" },
        { value: "urban_school", text: "Urban School" },
        { value: "suburban_school", text: "Suburban School" },
        { value: "pre_service_teacher_or_training_program", text: "Pre-service/Teacher training program" },
      ],
      isRequired: true,
    },
  ],
};

const dailyExperiencePage = {
  title: "Your Daily Experience",
  description: "We want to understand the day-to-day realities of your teaching experience.",
  elements: [
    {
      name: "daily_experience__energy_capacity",
      title: "Thinking about a typical work week, which statement best describes your energy and capacity?",
      type: "radiogroup",
      choices: [
        { value: 1, text: "Thriving: I feel energized and able to handle the challenges of teaching." },
        { value: 2, text: "Managing: I'm juggling a lot, but I'm keeping my head above water." },
        { value: 3, text: "Strained: I often feel drained and am struggling to keep up." },
        { value: 4, text: "Overwhelmed: I feel completely exhausted and that the demands are unsustainable." },
      ],
      isRequired: true,
    },
    {
      name: "daily_experience__energy_impact_factors",
      title: "What most impacts your energy and well-being at work? (Select up to 3)",
      type: "checkbox",
      choices: [
        { value: "student_behavior", text: "Student behavior" },
        { value: "parent_communication", text: "Parent communication" },
        { value: "planning_and_prep_load", text: "Planning and prep load" },
        { value: "administrative_tasks_paperwork", text: "Administrative tasks/paperwork" },
        { value: "juggling_competing_priorities", text: "Juggling competing priorities" },
        { value: "lack_of_time", text: "Lack of time" },
        { value: "emotional_demands_of_the_job", text: "Emotional demands of the job" },
      ],
      validators: [
        {
          type: "answercount",
          minCount: 1,
          maxCount: 3,
        },
      ],
      isRequired: true,
    },
    {
      name: "daily_experience__is_workload_manageable",
      title: "My workload feels manageable.",
      type: "rating",
      rateType: "smileys",
      rateMin: 1,
      rateMax: 5,
      minRateDescription: "Strongly Disagree",
      maxRateDescription: "Strongly Agree",
      isRequired: true,
    },
    {
      name: "daily_experience__biggest_challenge_to_well_being",
      title: "In your own words, what is the single biggest challenge to your well-being at work right now?",
      type: "comment",
      isRequired: true,
    },
  ],
};

const wellBeingPage = {
  title: "Your Well-Being",
  elements: [
    {
      title: "<b>Part A: General Well-being</b>",
      description:
        "<b class='font-semibold text-gray-600'>Instructions:</b> Over the <b class='font-semibold text-gray-600'>last 2 weeks</b>, how often have you been bothered by the following problems?",
      type: "matrix",
      name: "well_being__general",
      columns: [
        {
          value: 0,
          text: "Not at all",
        },
        {
          value: 1,
          text: "Several days",
        },
        {
          value: 2,
          text: "More than half the days",
        },
        {
          value: 3,
          text: "Nearly every day",
        },
      ],
      rows: [
        {
          value: "nervous_anxious_on_edge",
          text: "Feeling nervous, anxious, or on edge",
        },
        {
          value: "worrying_control",
          text: "Not being able to stop or control worrying",
        },
        {
          value: "worrying_too_much",
          text: "Worrying too much about different things",
        },
        {
          value: "trouble_relaxing",
          text: "Trouble relaxing",
        },
        {
          value: "restless_sit_still",
          text: "Being so restless that it is hard to sit still",
        },
        {
          value: "annoyed_irritable",
          text: "Becoming easily annoyed or irritable",
        },
        {
          value: "afraid_terrible",
          text: "Feeling afraid as if something awful might happen",
        },
      ],
      alternateRows: true,
      eachRowRequired: true,
    },
    {
      title: "<b>Part B: Work-Related Well-being</b>",
      description:
        "<b class='font-semibold text-gray-600'>Instructions:</b> The following statements are about your relationship with your work. Please indicate how you feel on the scale provided.",
      type: "matrix",
      name: "well_being__work_related",
      columns: [
        {
          value: 1,
          text: "Strongly Disagree",
        },
        {
          value: 2,
          text: "Disagree",
        },
        {
          value: 3,
          text: "Neutral",
        },
        {
          value: 4,
          text: "Agree",
        },
        {
          value: 5,
          text: "Strongly Agree",
        },
      ],
      rows: [
        {
          value: "emotional_drained",
          text: "I end most workdays feeling emotionally drained.",
        },
        {
          value: "cynical_potential",
          text: "I have become more cynical about the potential of my work.",
        },
        {
          value: "confident_effective",
          text: " I feel confident in my effectiveness as a teacher.",
        },
        {
          value: "practical_strategies",
          text: "I have practical strategies to regulate my stress during the day.",
        },
      ],
      alternateRows: true,
      eachRowRequired: true,
    },
  ],
};

const confidenceInTeachingPage = {
  title: "Your Confidence in Teaching",
  elements: [
    {
      title: "<b>Your Confidence in Teaching</b>",
      description: `<b class="font-semibold text-gray-600">Instructions:</b> This section is designed to help us gain a better understanding of the kinds of things that create difficulties for teachers. Please indicate your opinion about each of the statements below. <b class="font-semibold text-gray-600">How much can you do?</b>`,
      name: "confidence_in_teaching",
      type: "matrix",
      columns: [
        { value: 1, text: "Nothing (1)" },
        { value: 2, text: "Very Little (2-3)" },
        { value: 3, text: "Some Influence (4-6)" },
        { value: 4, text: "Quite a Bit (7-8)" },
        { value: 5, text: "A Great Deal (9)" },
      ],
      rows: [
        {
          value: "control_disruptive_behavior",
          text: "How much can you do to control disruptive behavior in the classroom?",
        },
        {
          value: "motivate_low_interest",
          text: "How much can you do to motivate students who show low interest in school work?",
        },
        {
          value: "believe_can_do_well",
          text: "How much can you do to get students to believe they can do well in school work?",
        },
        {
          value: "help_students_value_learning",
          text: "How much can you do to help your students value learning?",
        },
        {
          value: "craft_good_questions",
          text: "To what extent can you craft good questions for your students?",
        },
        {
          value: "get_children_follow_rules",
          text: "How much can you do to get children to follow classroom rules?",
        },
        {
          value: "calm_disruptive_student",
          text: "How much can you do to calm a student who is disruptive or noisy?",
        },
        {
          value: "establish_classroom_management",
          text: "How well can you establish a classroom management system with each group of students?",
        },
        {
          value: "use_variety_assessment",
          text: "How much can you use a variety of assessment strategies?",
        },
        {
          value: "provide_alternative_explanation",
          text: "To what extent can you provide an alternative explanation or example when students are confused?",
        },
        {
          value: "assist_families_do_well",
          text: "How much can you assist families in helping their children do well in school?",
        },
        {
          value: "implement_alternative_strategies",
          text: "How well can you implement alternative strategies in your classroom?",
        },
      ],
      alternateRows: true,
      eachRowRequired: true,
    },
  ],
};

const goalsIntentionsPage = {
  navigationTitle: "Your Goals & Intentions",
  title: "Your Goals & Intentions",
  elements: [
    {
      type: "checkbox",
      name: "goals_intentions",
      description: "Please select up to 3",
      title: "Which of these areas are your top priorities for support right now?",
      choices: [
        { value: "classroom_management", text: "Classroom management" },
        { value: "de_escalating_oppositional_behaviors", text: "De-escalating oppositional behaviors" },
        { value: "trauma_informed_strategies", text: "Trauma-informed strategies" },
        { value: "building_parent_partnerships", text: "Building parent partnerships" },
        { value: "social_emotional_learning", text: "Social-Emotional Learning (SEL)" },
        { value: "work_life_balance", text: "Work-life balance" },
        {
          value: "reconnecting_with_identity_purpose",
          text: "Reconnecting with my identity/purpose as an educator",
        },
      ],
      isRequired: true,
      validators: [
        {
          type: "answercount",
          minCount: 1,
          maxCount: 3,
        },
      ],
    },
    {
      title: "Do you see yourself teaching in this role next school year?",
      type: "radiogroup",
      name: "goals_intentions__teaching_next_year",
      choices: [
        { value: 1, text: "Yes" },
        { value: 2, text: "I'm unsure" },
        { value: 3, text: "No" },
      ],
      isRequired: true,
    },
    {
      title: " I feel I have the support I need to maintain my well-being at work.",
      name: "goals_intentions__heybodhi_wellbeing_support",
      type: "rating",
      rateType: "smileys",
      rateMin: 1,
      rateMax: 5,
      minRateDescription: "Strongly Disagree",
      maxRateDescription: "Strongly Agree",
      isRequired: true,
    },
    {
      title: "If you could change one thing about your professional life in the next 8 weeks, what would it be?",
      name: "goals_intentions__heybodhi_one_thing",
      type: "comment",
      isRequired: true,
    },
    {
      title: "I feel emotionally ready for my first placement.",
      name: "goals_intentions__emotional_ready_first_placement",
      type: "rating",
      rateType: "smileys",
      rateMin: 1,
      rateMax: 5,
      minRateDescription: "Strongly Disagree",
      maxRateDescription: "Strongly Agree",
      isRequired: true,
      visibleIf: "{teaching_context__primary_role} = 'pre_service_or_student_teacher'",
    },
    {
      title: "I know what to try when I feel overwhelmed during my practicum.",
      name: "goals_intentions__know_what_to_try_when_overwhelmed",
      type: "rating",
      rateType: "smileys",
      rateMin: 1,
      rateMax: 5,
      minRateDescription: "Strongly Disagree",
      maxRateDescription: "Strongly Agree",
      isRequired: true,
      visibleIf: "{teaching_context__primary_role} = 'pre_service_or_student_teacher'",
    },
  ],
};

export const surveyJson = {
  title: "HeyBodhi Pilot: Teacher Growth and Well-being Survey",
  completedHtml: `
    <h3 style="margin-bottom: 28px;">Thank you for your time and thoughtful responses!</h3>
    <p style="font-size: 20px;">You're all set to begin using HeyBodhi. Redirecting...</p>
    `,
  pages: [
    welcomePage,
    teachingContextPage,
    dailyExperiencePage,
    wellBeingPage,
    confidenceInTeachingPage,
    goalsIntentionsPage,
  ],
};
